import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Html5Qrcode } from "html5-qrcode";
import { X as CloseIcon, Camera, ScanLine, AlertTriangle } from "lucide-react";
import { useI18n } from "../lib/i18n";

/**
 * QR scanner modal — uses html5-qrcode to open the device camera and decode
 * a QR value. Calls `onResult(url)` with the decoded URL/text.
 *
 * Automatically stops the camera on unmount so no stray tracks stay open.
 */
export default function QrScannerModal({ open, onClose, onResult }) {
  const { lang } = useI18n();
  const elId = "cartal-qr-reader";
  const scannerRef = useRef(null);
  const [err, setErr] = useState(null);
  const [starting, setStarting] = useState(true);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setErr(null);
    setStarting(true);

    const start = async () => {
      try {
        const scanner = new Html5Qrcode(elId, { verbose: false });
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: (w, h) => {
              const side = Math.min(w, h) * 0.75;
              return { width: side, height: side };
            },
            aspectRatio: 1.0,
          },
          (decodedText) => {
            if (cancelled) return;
            cancelled = true;
            try { scanner.stop(); } catch { /* noop */ }
            onResult && onResult(decodedText);
          },
          () => { /* onScanFailure — noop, prevents log spam */ },
        );
        if (!cancelled) setStarting(false);
      } catch (e) {
        if (!cancelled) {
          setStarting(false);
          setErr(
            lang === "ar"
              ? "تعذّر فتح الكاميرا — تأكد من منح الإذن ثم أعد المحاولة."
              : "Could not open the camera — please grant permission and retry.",
          );
        }
      }
    };
    start();

    return () => {
      cancelled = true;
      const s = scannerRef.current;
      if (s) {
        try {
          if (s.isScanning) s.stop().then(() => { try { s.clear(); } catch { /* noop */ } });
          else { try { s.clear(); } catch { /* noop */ } }
        } catch { /* noop */ }
      }
    };
  }, [open, lang, onResult]);

  const handleClose = () => {
    // Stop camera immediately for responsive close UX
    const s = scannerRef.current;
    if (s) {
      try {
        if (s.isScanning) {
          s.stop().then(() => { try { s.clear(); } catch { /* noop */ } }).catch(() => { /* noop */ });
        } else {
          try { s.clear(); } catch { /* noop */ }
        }
      } catch { /* noop */ }
    }
    onClose && onClose();
  };

  if (!open) return null;

  const modalContent = (
    <div
      className="fixed inset-0 z-[100] overflow-y-auto"
      style={{ backgroundColor: "rgba(10,10,10,0.92)", backdropFilter: "blur(10px)" }}
      onClick={handleClose}
      data-testid="qr-scanner-modal"
      role="dialog"
      aria-modal="true"
    >
      {/* Floating top-right close button (always reachable) */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); handleClose(); }}
        className="fixed top-4 end-4 z-[110] w-11 h-11 rounded-full bg-white/95 hover:bg-white shadow-2xl flex items-center justify-center transition-all hover:scale-105"
        aria-label={lang === "ar" ? "إغلاق الماسح" : "Close scanner"}
        data-testid="qr-scanner-close-floating"
      >
        <CloseIcon className="w-5 h-5 text-[#0a0a0a]" strokeWidth={2.5} />
      </button>

      <div className="min-h-full flex items-center justify-center p-3 sm:p-6 py-16 sm:py-8">
        <div
          className="relative bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#0a0a0a]/10 bg-gradient-to-b from-[#faf8f1] to-white">
          <div className="inline-flex items-center gap-2 text-sm font-bold text-[#0a0a0a]">
            <ScanLine className="w-4 h-4 text-[#8a6f2e]" />
            {lang === "ar" ? "مسح رمز QR" : "Scan QR code"}
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0a0a0a] text-white hover:bg-[#1a1a1a] transition-colors text-xs font-bold"
            aria-label={lang === "ar" ? "إغلاق" : "Close"}
            data-testid="qr-scanner-close"
          >
            <CloseIcon className="w-3.5 h-3.5" />
            {lang === "ar" ? "إغلاق" : "Close"}
          </button>
        </div>

        {/* Camera viewport */}
        <div className="relative bg-[#0a0a0a] aspect-square w-full">
          <div id={elId} className="absolute inset-0" data-testid="qr-scanner-viewport" />
          {starting && !err && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white/85 text-sm gap-2 pointer-events-none">
              <Camera className="w-8 h-8 animate-pulse" />
              <span>{lang === "ar" ? "جاري تشغيل الكاميرا…" : "Starting camera…"}</span>
            </div>
          )}
          {err && (
            <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center text-white/90 bg-black/60">
              <AlertTriangle className="w-8 h-8 mb-2 text-amber-400" />
              <p className="text-sm leading-relaxed">{err}</p>
            </div>
          )}
        </div>

        {/* Instructions + prominent Cancel button */}
        <div className="p-4 space-y-3">
          <p className="text-xs text-[#6b6b6b] leading-relaxed">
            {lang === "ar"
              ? "وجّه الكاميرا نحو رمز QR الخاص بإعلان كارتال (على لوحة معرض أو رسالة واتساب) — سيتم فتح الإعلان تلقائياً."
              : "Point your camera at a CARTAL ad's QR code (on a showroom sign or WhatsApp) — the listing opens automatically."}
          </p>
          <button
            type="button"
            onClick={handleClose}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white font-bold text-sm transition-all"
            data-testid="qr-scanner-cancel-btn"
          >
            <CloseIcon className="w-4 h-4" />
            {lang === "ar" ? "إغلاق الكاميرا" : "Close camera"}
          </button>
        </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
